import { ReactNode } from "react"
import { useFormikContext } from "formik"
import { AnimatePresence, motion, Target } from "@/lib/animations"
import { useTrans } from "@/i18n"
import { resolveFieldError } from "@/lib/formik"
import { cn } from "@/lib/utils"

export function FormikError<Data extends object | string>({
	field,
	reliesOnField,
	namespace = "common",
}: {
	field: Data extends string ? Data : keyof Data
	reliesOnField?: Data extends string ? Data : keyof Data
	namespace?: string
}) {
	const t = useTrans(namespace)
	const { touched, errors } = useFormikContext<any>()

	let error = touched[field] ? resolveFieldError(t, errors[field]) : null

	if (reliesOnField) {
		if (!touched[reliesOnField]) {
			error = null
		}
	}

	return <InlineError error={error} />
}

interface ErrorProps {
	error?: ReactNode | null
	initial?: Target
	animate?: Target
	exit?: Target
	className?: string
}

export const FormError = ({
	error,
	initial = {},
	animate = {},
	exit = {},
	className,
}: ErrorProps) => {
	return (
		<AnimatePresence>
			{error && (
				<motion.p
					className={cn("text-sm text-red-600", className)}
					initial={{ ...initial, opacity: 0 }}
					animate={{ ...animate, opacity: 1 }}
					exit={{ ...exit, opacity: 0 }}
					transition={{ bounce: 0 }}
				>
					{error}
				</motion.p>
			)}
		</AnimatePresence>
	)
}

export const InlineError = ({ error }: { error?: string | null }) => {
	return (
		<FormError
			error={error}
			initial={{ marginTop: `0rem` }}
			animate={{
				marginTop: `0.75rem`,
			}}
			exit={{
				marginTop: `0rem`,
			}}
		/>
	)
}
